import React, { useMemo, useRef, useState } from "react";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";

import {
  ApiErrorBanner,
  AutoComplete,
  Button,
  Dialog,
  showNotification,
} from "fe-shared/src/components";
import { CloseIcon } from "fe-shared/src/components/Icons";
import { ApiError } from "fe-shared/src/types/common";

import { PickupRequestDetails, PickupRequestStatus } from "../../types";
import {
  PickupRequestAssignmentRole,
  canWithdrawCarrier,
  canWithdrawHauler,
} from "../../helpers/pickupRequestStatus";
import { getPickupRequestCarriers } from "../../api/pickupRequestCarriers";
import { getPickupRequestHaulers } from "../../api/pickupRequestHaulers";
import {
  assignPickupRequestCarrier,
  removePickupRequestCarrier,
  removePickupRequestHauler,
  replacePickupRequestHauler,
} from "../../api/pickupRequests";

import styles from "./EditAssignmentDialog.module.scss";

interface Option {
  value: string | number;
  label: string;
}

const NOT_SELECTED = "none";

const matchesQuery =(selected?: Option) => (
  option: Option,
  query: string
) =>
  query === selected?.label.toLowerCase() ||
  option.label.toLowerCase().includes(query);

export interface Props {
  request: PickupRequestDetails;
  callerRole: PickupRequestAssignmentRole;
  onClose: () => void;
  onSaved: () => void;
  onChanged?: () => void;
}

interface Assignment {
  haulerId: number | null;
  carrierId: number | null;
}

const EditAssignmentDialog = (props: Props) => {
  const { request, callerRole, onClose, onSaved, onChanged } = props;
  const { t } = useTranslation();
  const { requestNumber } = request;

  const [haulerId, setHaulerId] = useState(request.targetCompanyId);
  const [carrierId, setCarrierId] = useState(request.assignedCarrierId);
  const [isConfirming, setIsConfirming] = useState(false);
  // Moves forward as steps land, so a retry after a half-done save does not repeat them.
  const applied = useRef<Assignment>({
    haulerId: request.targetCompanyId,
    carrierId: request.assignedCarrierId,
  });
  const [, setAppliedVersion] = useState(0);
  const markApplied = (next: Assignment) => {
    applied.current = next;
    setAppliedVersion((version) => version + 1);
  };

  const isHaulerEditable = canWithdrawHauler(request.status, callerRole);
  const isCarrierEditable = canWithdrawCarrier(request.status, callerRole);

  const haulersQuery = useQuery(
    ["pickupRequestHaulers", request.sourceCompanyId],
    () => getPickupRequestHaulers(request.sourceCompanyId as number),
    {
      enabled: isHaulerEditable && !!request.sourceCompanyId,
      refetchOnWindowFocus: false,
      onError: () => {
        showNotification(
          t("pickupRequests.assignment.haulersLoadError"),
          undefined,
          CloseIcon
        );
      },
    }
  );
  const carriersQuery = useQuery(
    ["pickupRequestCarriers", haulerId],
    () => getPickupRequestCarriers(haulerId as number),
    {
      enabled: isCarrierEditable && !!haulerId,
      refetchOnWindowFocus: false,
      onError: () => {
        showNotification(
          t("pickupRequests.details.carriersLoadError"),
          undefined,
          CloseIcon
        );
      },
    }
  );

  const currentHauler = request.targetCompanyId;
  const currentCarrier = request.assignedCarrierId;
  const currentCarrierName = [request.carrierFirstName, request.carrierLastName]
    .filter(Boolean)
    .join(" ");

  const haulerOptions = useMemo(() => {
    const options = (haulersQuery.data ?? []).map((hauler) => ({
      value: hauler.id,
      label: hauler.registrationNumber
        ? `${hauler.name} (${hauler.registrationNumber})`
        : hauler.name,
    }));
    const currentLabel = request.target?.displayName;

    return currentHauler &&
      currentLabel &&
      !options.some((o) => o.value === currentHauler)
      ? [{ value: currentHauler, label: currentLabel }, ...options]
      : options;
  }, [haulersQuery.data, currentHauler, request.target]);
  const carrierOptions = useMemo(() => {
    const options = (carriersQuery.data ?? []).map((carrier) => ({
      value: carrier.id,
      label: carrier.name,
    }));

    // Only the hauler the carrier belongs to may offer it as a fallback.
    return haulerId === currentHauler &&
      currentCarrier &&
      currentCarrierName &&
      !options.some((o) => o.value === currentCarrier)
      ? [{ value: currentCarrier, label: currentCarrierName }, ...options]
      : options;
  }, [
    carriersQuery.data,
    haulerId,
    currentHauler,
    currentCarrier,
    currentCarrierName,
  ]);

  const selectedHauler = haulerOptions.find((o) => o.value === haulerId);
  const selectedCarrier = carrierOptions.find((o) => o.value === carrierId);

  const notSelected = {
    value: NOT_SELECTED,
    label: t("pickupRequests.form.notSelected"),
  };
  const withNotSelected = (options: Option[], isSelected: boolean) =>
    isSelected ? [notSelected, ...options] : options;

  const [save, saveResult] = useMutation<void, AxiosError<ApiError>, string>(
    async (number) => {
      if (haulerId !== applied.current.haulerId) {
        if (
          haulerId === null &&
          request.status !== PickupRequestStatus.InReview
        ) {
          await removePickupRequestHauler(number);
        } else {
          await replacePickupRequestHauler(number, haulerId);
        }

        markApplied({ haulerId, carrierId: null });
      }

      if (carrierId !== applied.current.carrierId) {
        if (carrierId === null) {
          await removePickupRequestCarrier(number);
        } else {
          if (
            request.status === PickupRequestStatus.Requested &&
            applied.current.carrierId !== null
          ) {
            await removePickupRequestCarrier(number);
            markApplied({ haulerId, carrierId: null });
          }

          await assignPickupRequestCarrier(number, carrierId);
        }

        markApplied({ haulerId, carrierId });
      }
    },
    {
      onSuccess: onSaved,
      onError: () => {
        setIsConfirming(false);

        if (
          applied.current.haulerId !== request.targetCompanyId ||
          applied.current.carrierId !== request.assignedCarrierId
        ) {
          onChanged?.();
        }
      },
    }
  );

  const isDirty =
    haulerId !== applied.current.haulerId ||
    carrierId !== applied.current.carrierId;
  const needsConfirm =
    request.status === PickupRequestStatus.Requested &&
    applied.current.carrierId !== null &&
    isDirty;

  const handleHaulerChange = (value: unknown) => {
    const nextHaulerId = typeof value === "number" ? value : null;

    setHaulerId(nextHaulerId);
    setCarrierId(
      nextHaulerId === applied.current.haulerId
        ? applied.current.carrierId
        : null
    );
    setIsConfirming(false);
  };

  const handleCarrierChange = (value: unknown) => {
    setCarrierId(typeof value === "number" ? value : null);
    setIsConfirming(false);
  };

  const handleSave = () => {
    if (needsConfirm && !isConfirming) {
      setIsConfirming(true);
      return;
    }

    if (requestNumber) {
      save(requestNumber);
    }
  };

  return (
    <Dialog
      modal
      isActive
      width={528}
      title={t("pickupRequests.assignment.title")}
      onClose={onClose}
      actions={[
        <Button
          id="edit-assignment-submit"
          key="submit"
          variant="filled"
          disabled={!isDirty || saveResult.isLoading}
          onClick={handleSave}
        >
          {t(
            isConfirming
              ? "pickupRequests.assignment.confirm"
              : "pickupRequests.assignment.save"
          )}
        </Button>,
        <Button
          id="edit-assignment-cancel"
          key="cancel"
          variant="outlined"
          disabled={saveResult.isLoading}
          onClick={isConfirming ? () => setIsConfirming(false) : onClose}
        >
          {t(
            isConfirming
              ? "pickupRequests.assignment.back"
              : "pickupRequests.assignment.cancel"
          )}
        </Button>,
      ]}
    >
      {saveResult.isError && (
        <ApiErrorBanner
          id="edit-assignment-error-banner"
          requestStatus={saveResult}
        />
      )}
      {isConfirming ? (
        <p id="edit-assignment-consequence" className={styles.consequence}>
          {t("pickupRequests.assignment.requestedConsequence")}
        </p>
      ) : (
        <>
          {isHaulerEditable && (
            <AutoComplete
              id="edit-assignment-hauler"
              title={t("pickupRequests.form.haulerLabel")}
              placeholder={t("pickupRequests.form.haulerPlaceholder")}
              value={selectedHauler}
              filterOption={matchesQuery(selectedHauler)}
              options={withNotSelected(haulerOptions, haulerId !== null)}
              isLoading={haulersQuery.isLoading}
              isEmptyStateVisible
              disabled={saveResult.isLoading}
              wrapperClassName={styles.field}
              onChange={(_event, value) => handleHaulerChange(value)}
            />
          )}
          {isCarrierEditable && (
            <AutoComplete
              id="edit-assignment-carrier"
              title={t("pickupRequests.form.carrierLabel")}
              placeholder={t("pickupRequests.form.carrierPlaceholder")}
              value={selectedCarrier}
              filterOption={matchesQuery(selectedCarrier)}
              options={withNotSelected(carrierOptions, carrierId !== null)}
              isLoading={carriersQuery.isLoading}
              isEmptyStateVisible
              disabled={
                !haulerId || saveResult.isLoading || !carrierOptions.length
              }
              wrapperClassName={styles.field}
              onChange={(_event, value) => handleCarrierChange(value)}
            />
          )}
        </>
      )}
    </Dialog>
  );
};

export default EditAssignmentDialog;
